import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, Permission } from '../types';
import { logoutUser, refreshAccessToken } from '../api/auth/api';
import { useToast } from '../context/ToastContext';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// src/context/authContext.ts
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [permission, setPermission] = useState<Permission>({});
  const [rememberMe, setRememberMe] = useState<boolean>(false); // Store rememberMe state
  const [showChat, setShowChat] = useState<boolean>(false);
  const { showToast } = useToast();

  useEffect(() => {
    const token = rememberMe ? localStorage.getItem('authToken') : sessionStorage.getItem('authToken');
    const storedUser = rememberMe ? localStorage.getItem('userData') : sessionStorage.getItem('userData');
    const storedPermission = rememberMe
      ? localStorage.getItem('userPermission')
      : sessionStorage.getItem('userPermission');
    console.info({ token, storedUser, storedPermission });

    if (token && storedUser && storedPermission) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const parsedPermission = storedPermission ? JSON.parse(storedPermission) : {};
        setIsAuthenticated(true);
        setUser(parsedUser);
        setPermission(parsedPermission);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        setIsAuthenticated(false);
        setUser(null);
        setPermission({});
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setPermission({});
    }

    setIsInitialized(true);
  }, [rememberMe]);

  useEffect(() => {
    if (
      localStorage.getItem('authToken') &&
      localStorage.getItem('userData') &&
      localStorage.getItem('rememberMe') === 'true'
    ) {
      setIsAuthenticated(true);
      setRememberMe(true);
    }
  }, []);

  const login = (
    token: string,
    refreshToken: string | null,
    sessionKey: string | null,
    userData: User,
    userPermission: Permission,
    remember: boolean,
  ) => {
    console.info('Login now', { token, refreshToken, sessionKey, userData, userPermission, remember });

    if (remember) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('refreshToken', refreshToken || '');
      localStorage.setItem('sessionKey', sessionKey || '');
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('userPermission', JSON.stringify(userPermission));
      localStorage.setItem('rememberMe', 'true'); // Save rememberMe status in localStorage
    } else {
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('refreshToken', refreshToken || '');
      sessionStorage.setItem('sessionKey', sessionKey || '');
      sessionStorage.setItem('userData', JSON.stringify(userData));
      sessionStorage.setItem('userPermission', JSON.stringify(userPermission));
      localStorage.setItem('rememberMe', 'false'); // Save rememberMe status in localStorage
    }
    setRememberMe(remember);
    setIsAuthenticated(true);
    setUser(userData);
    setPermission(userPermission);
  };

  const customCustomerId = (customId: string) => {
    if (user && setUser) {
      setUser((prevUser) => {
        if (!prevUser) return prevUser;

        const updatedUser = {
          ...prevUser,
          customer_id: customId,
        };

        // Save updated user to storage
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('userData', JSON.stringify(updatedUser));

        return updatedUser;
      });
    }
  };

  const refreshAccessTokenFunction = async (): Promise<boolean> => {
    const refreshToken = rememberMe
      ? localStorage.getItem('refreshToken')
      : sessionStorage.getItem('refreshToken');
    const sessionKey = rememberMe ? localStorage.getItem('sessionKey') : sessionStorage.getItem('sessionKey');

    if (!refreshToken) {
      return false;
    }

    try {
      const response = await refreshAccessToken(refreshToken, sessionKey || undefined);

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('authToken', response.data);

      return true;
    } catch (error) {
      await logout();
      return false;
    }
  };

  const logout = async () => {
    const token = rememberMe ? localStorage.getItem('authToken') : sessionStorage.getItem('authToken');
    const refreshToken = rememberMe
      ? localStorage.getItem('refreshToken')
      : sessionStorage.getItem('refreshToken');
    const sessionKey = rememberMe ? localStorage.getItem('sessionKey') : sessionStorage.getItem('sessionKey');
    const storedUser = rememberMe ? localStorage.getItem('userData') : sessionStorage.getItem('userData');

    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        await logoutUser(userData.id, token, refreshToken || undefined, sessionKey || undefined);
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionKey');
        localStorage.removeItem('userData');
        localStorage.removeItem('userPermission');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('sessionKey');
        sessionStorage.removeItem('userData');
        sessionStorage.removeItem('userPermission');
        sessionStorage.removeItem('selectedFleetManager');
        sessionStorage.removeItem('customCustomerId');
        sessionStorage.removeItem('selectedFleetManagerUsername');
        setIsAuthenticated(false);
        setUser(null);
        setPermission({});
        setRememberMe(false);
        setShowChat(false);
      } catch (error) {
        if (error instanceof Error) {
          showToast(error.message, 'error');
        }
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isInitialized,
        user,
        permission,
        login,
        logout,
        refreshAccessToken: refreshAccessTokenFunction,
        rememberMe,
        showChat,
        setShowChat,
        customCustomerId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
